"use client";

import { c as _c } from "react/compiler-runtime";
import type { ComponentPropsWithRef } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useAnimationContext } from "@/context/animation-context";
import { cn } from "@/lib/utils";
export interface TypewriterProps extends ComponentPropsWithRef<typeof motion.div> {
  text: string;
  delay?: number;
  onTypingComplete?: () => void;
  lineNumber: number;
  isActive: boolean;
  totalLines: number;
  isCurrentLine: boolean;
  isReplaying?: boolean;
  onHeightChange: (height: number) => void;
  isMobile: boolean;
}
function Typewriter(t0) {
  const $ = _c(68);
  let className;
  let isActive;
  let isCurrentLine;
  let isMobile;
  let isReplaying;
  let lineNumber;
  let onAnimationComplete;
  let onHeightChange;
  let onTypingComplete;
  let props;
  let t1;
  let text;
  let totalLines;
  if ($[0] !== t0) {
    ({
      text,
      delay: t1,
      className,
      onTypingComplete,
      onAnimationComplete,
      isActive,
      isCurrentLine,
      lineNumber,
      totalLines,
      isReplaying,
      onHeightChange,
      isMobile,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = isActive;
    $[3] = isCurrentLine;
    $[4] = isMobile;
    $[5] = isReplaying;
    $[6] = lineNumber;
    $[7] = onAnimationComplete;
    $[8] = onHeightChange;
    $[9] = onTypingComplete;
    $[10] = props;
    $[11] = t1;
    $[12] = text;
    $[13] = totalLines;
  } else {
    className = $[1];
    isActive = $[2];
    isCurrentLine = $[3];
    isMobile = $[4];
    isReplaying = $[5];
    lineNumber = $[6];
    onAnimationComplete = $[7];
    onHeightChange = $[8];
    onTypingComplete = $[9];
    props = $[10];
    t1 = $[11];
    text = $[12];
    totalLines = $[13];
  }
  const delay = t1 === undefined ? 50 : t1;
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const textRef = useRef(null);
  const {
    animationStates,
    setAnimationComplete
  } = useAnimationContext();
  let t2;
  let t3;
  if ($[14] !== onHeightChange) {
    t2 = () => {
      if (textRef.current != null) {
        onHeightChange(textRef.current.offsetHeight);
      }
    };
    t3 = [onHeightChange, textRef];
    $[14] = onHeightChange;
    $[15] = t2;
    $[16] = t3;
  } else {
    t2 = $[15];
    t3 = $[16];
  }
  useLayoutEffect(t2, t3);
  let t4;
  let t5;
  if ($[17] !== isReplaying) {
    t4 = () => {
      if (isReplaying) {
        setDisplayText("");
        setCurrentIndex(0);
      }
    };
    t5 = [isReplaying];
    $[17] = isReplaying;
    $[18] = t4;
    $[19] = t5;
  } else {
    t4 = $[18];
    t5 = $[19];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[20] !== isActive) {
    t6 = () => {
      if (isActive) {
        setDisplayText("");
        setCurrentIndex(0);
      }
    };
    t7 = [isActive];
    $[20] = isActive;
    $[21] = t6;
    $[22] = t7;
  } else {
    t6 = $[21];
    t7 = $[22];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[23] !== animationStates.hasTypewriterPlayed || $[24] !== currentIndex || $[25] !== delay || $[26] !== isActive || $[27] !== lineNumber || $[28] !== onTypingComplete || $[29] !== setAnimationComplete || $[30] !== text || $[31] !== totalLines) {
    t8 = () => {
      if (!animationStates.hasTypewriterPlayed && isActive) {
        if (currentIndex < text.length) {
          const timeout = setTimeout(() => {
            setDisplayText(prev => prev + text[currentIndex]);
            setCurrentIndex(_temp);
          }, delay);
          return () => clearTimeout(timeout);
        } else {
          if (currentIndex === text.length) {
            if (onTypingComplete) {
              onTypingComplete();
            }
            if (lineNumber === totalLines) {
              setAnimationComplete("hasTypewriterPlayed");
            }
          }
        }
      }
    };
    t9 = [currentIndex, delay, text, onTypingComplete, animationStates.hasTypewriterPlayed, setAnimationComplete, lineNumber, isActive, totalLines];
    $[23] = animationStates.hasTypewriterPlayed;
    $[24] = currentIndex;
    $[25] = delay;
    $[26] = isActive;
    $[27] = lineNumber;
    $[28] = onTypingComplete;
    $[29] = setAnimationComplete;
    $[30] = text;
    $[31] = totalLines;
    $[32] = t8;
    $[33] = t9;
  } else {
    t8 = $[32];
    t9 = $[33];
  }
  useEffect(t8, t9);
  if (animationStates.hasTypewriterPlayed && !isReplaying) {
    let t10;
    if ($[34] !== className) {
      t10 = cn(className, "block w-full");
      $[34] = className;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    let t11;
    if ($[36] !== t10 || $[37] !== text) {
      t11 = <div className={t10}>{text}</div>;
      $[36] = t10;
      $[37] = text;
      $[38] = t11;
    } else {
      t11 = $[38];
    }
    return t11;
  }
  const t10 = `${lineNumber}-${isReplaying}`;
  let t11;
  if ($[39] !== className) {
    t11 = cn("block w-full", className);
    $[39] = className;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  let t13;
  let t14;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      opacity: 0
    };
    t13 = {
      opacity: 1
    };
    t14 = {
      duration: 0.5
    };
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t12 = $[41];
    t13 = $[42];
    t14 = $[43];
  }
  let t15;
  if ($[44] !== isCurrentLine || $[45] !== isMobile) {
    t15 = isCurrentLine && ["after:bg-primary after:absolute after:ml-[0.0625rem] after:inline-block after:w-[0.15em] after:content-['']", isMobile ? "after:animate-cursor-blink-mobile after:h-[1.1em] after:-translate-y-[0.05em]" : "after:animate-cursor-blink after:h-[1.25em] after:-translate-y-[0.125em]"];
    $[44] = isCurrentLine;
    $[45] = isMobile;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== t15) {
    t16 = cn("relative inline-block break-words whitespace-pre-wrap", t15);
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const t17 = isActive ? "auto" : "0ch";
  const t18 = `width ${delay}ms step-end`;
  let t19;
  if ($[49] !== t17 || $[50] !== t18) {
    t19 = {
      width: t17,
      transition: t18
    };
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  let t21;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      opacity: 0
    };
    t21 = {
      opacity: 1
    };
    $[52] = t20;
    $[53] = t21;
  } else {
    t20 = $[52];
    t21 = $[53];
  }
  let t22;
  if ($[54] !== displayText || $[55] !== t16 || $[56] !== t19) {
    t22 = <motion.span className={t16} style={t19} initial={t20} animate={t21}>{displayText}</motion.span>;
    $[54] = displayText;
    $[55] = t16;
    $[56] = t19;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== isActive || $[59] !== text) {
    t23 = !isActive && <span className="absolute h-0 overflow-hidden opacity-0" aria-hidden="true">{text}</span>;
    $[58] = isActive;
    $[59] = text;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== onAnimationComplete || $[62] !== props || $[63] !== t10 || $[64] !== t11 || $[65] !== t22 || $[66] !== t23) {
    t24 = <AnimatePresence><motion.div key={t10} className={t11} initial={t12} animate={t13} transition={t14} onAnimationComplete={onAnimationComplete} {...props}>{t22}{t23}</motion.div></AnimatePresence>;
    $[61] = onAnimationComplete;
    $[62] = props;
    $[63] = t10;
    $[64] = t11;
    $[65] = t22;
    $[66] = t23;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  return t24;
}
function _temp(prev_0) {
  return prev_0 + 1;
}
Typewriter.displayName = "Typewriter";
export default Typewriter;